import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, PanelsTopLeft, ShowerHead, Layers, Building2, Droplets, ArrowRight, RotateCcw, ShieldCheck, Clock, Wallet, Wrench, Sparkles } from "lucide-react";
import PageHero from "@/components/PageHero";
import { IMG } from "@/data/site";
import { useLead } from "@/context/LeadContext";

const AREAS = [
  { id: "Roof", icon: Home },
  { id: "Wall", icon: PanelsTopLeft },
  { id: "Bathroom", icon: ShowerHead },
  { id: "Terrace", icon: Layers },
  { id: "Basement", icon: Building2 },
  { id: "Tank", icon: Droplets },
];

const SEVERITY = ["Minor", "Moderate", "Severe"];

const KB = {
  Roof: { solution: "APP Membrane + Heat Reflective Coating", timeline: "2–3 Days", budget: "₹45–75 / sq.ft", service: "Roof Waterproofing" },
  Wall: { solution: "Injection DPC + Elastomeric Exterior Coating", timeline: "1–3 Days", budget: "₹40–70 / sq.ft", service: "Wall Damp Proofing" },
  Bathroom: { solution: "Nano-Crystalline + Epoxy Coving (no tile breaking)", timeline: "1–2 Days", budget: "₹90–150 / sq.ft", service: "Bathroom Waterproofing" },
  Terrace: { solution: "Fiber-reinforced PU Membrane + Slope Correction", timeline: "2–4 Days", budget: "₹55–90 / sq.ft", service: "Terrace Waterproofing" },
  Basement: { solution: "Crystalline + PU Injection Grouting", timeline: "3–5 Days", budget: "₹75–130 / sq.ft", service: "Basement Waterproofing" },
  Tank: { solution: "Food-grade Epoxy Lining + Joint Treatment", timeline: "1–2 Days", budget: "₹70–120 / sq.ft", service: "Water Tank Waterproofing" },
};

export default function LeakDetection() {
  const [step, setStep] = useState(0);
  const [area, setArea] = useState(null);
  const [severity, setSeverity] = useState(null);
  const { openLead } = useLead();

  const reset = () => { setStep(0); setArea(null); setSeverity(null); };
  const rec = area ? KB[area] : null;

  return (
    <div data-testid="leak-detection-page">
      <PageHero
        title="Smart Leak Detection Tool"
        subtitle="Answer two quick questions and get an instant, engineer-informed recommendation — the likely solution, timeline and budget range."
        image={IMG.waterMacro}
        crumbs={[{ label: "Leak Detection" }]}
      />

      <section className="bg-soft-grey py-20 md:py-28 min-h-[60vh]">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="flex items-center justify-center gap-2 mb-10">
            {[0, 1, 2].map((s) => (
              <div key={s} className={`h-1.5 rounded-full transition-all duration-500 ${step >= s ? "bg-royal w-12" : "bg-slate-300 w-6"}`} />
            ))}
          </div>

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="q1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
                <h2 className="font-display text-3xl font-extrabold text-navy-deep text-center mb-2">Where is your leakage?</h2>
                <p className="text-slate-500 text-center mb-8">Select the affected area</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {AREAS.map((a) => (
                    <button key={a.id} data-testid={`leak-area-${a.id}`} onClick={() => { setArea(a.id); setStep(1); }} className="group bg-white rounded-lg p-6 border border-slate-200 hover:border-royal hover:shadow-lg hover:-translate-y-1 transition-all">
                      <a.icon className="h-9 w-9 text-royal mx-auto group-hover:scale-110 transition-transform" />
                      <div className="font-semibold text-navy-deep mt-3">{a.id}</div>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="q2" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
                <h2 className="font-display text-3xl font-extrabold text-navy-deep text-center mb-2">How severe is it?</h2>
                <p className="text-slate-500 text-center mb-8">This helps us tailor the recommendation</p>
                <div className="space-y-3">
                  {SEVERITY.map((s) => (
                    <button key={s} data-testid={`leak-severity-${s}`} onClick={() => { setSeverity(s); setStep(2); }} className="w-full bg-white rounded-lg p-5 border border-slate-200 hover:border-royal hover:shadow-lg transition-all text-left flex items-center justify-between">
                      <span className="font-semibold text-navy-deep">{s}</span>
                      <ArrowRight className="h-5 w-5 text-royal" />
                    </button>
                  ))}
                </div>
                <button onClick={() => setStep(0)} className="mt-6 mx-auto flex items-center gap-2 text-slate-500 text-sm hover:text-royal"><RotateCcw className="h-4 w-4" /> Back</button>
              </motion.div>
            )}

            {step === 2 && rec && (
              <motion.div key="result" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="bg-navy-deep rounded-2xl p-8 md:p-10 text-white relative overflow-hidden" data-testid="leak-result">
                <div className="absolute -top-16 -right-16 h-48 w-48 bg-royal/30 blur-3xl rounded-full" />
                <div className="flex items-center gap-2 text-sky mb-3 relative"><Sparkles className="h-5 w-5" /><span className="text-xs font-semibold tracking-widest uppercase">Recommendation</span></div>
                <h2 className="font-display text-3xl font-extrabold relative">For your <span className="text-gradient-blue">{area}</span> ({severity})</h2>
                <div className="grid sm:grid-cols-3 gap-4 mt-8 relative">
                  <Card icon={Wrench} label="Suggested Solution" value={rec.solution} />
                  <Card icon={Clock} label="Expected Timeline" value={rec.timeline} />
                  <Card icon={Wallet} label="Budget Range" value={rec.budget} />
                </div>
                <div className="flex items-center gap-2 mt-6 text-slate-300 text-sm relative"><ShieldCheck className="h-4 w-4 text-gold" /> Backed by up to 15-year warranty</div>
                <div className="flex flex-wrap gap-3 mt-8 relative">
                  <button onClick={() => openLead("book_inspection", { prefill: { service: rec.service, message: `Leak tool: ${area} - ${severity}` } })} className="btn-ripple bg-royal hover:bg-royal/90 text-white font-semibold px-6 py-3 rounded-sm flex items-center gap-2 transition-colors" data-testid="leak-book">
                    Book Free Inspection <ArrowRight className="h-4 w-4" />
                  </button>
                  <button onClick={reset} className="glass text-white font-semibold px-6 py-3 rounded-sm hover:bg-white/10 transition-colors flex items-center gap-2"><RotateCcw className="h-4 w-4" /> Start Over</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  );
}

const Card = ({ icon: Icon, label, value }) => (
  <div className="glass rounded-lg p-5">
    <Icon className="h-6 w-6 text-sky mb-2" />
    <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
    <div className="font-semibold mt-1">{value}</div>
  </div>
);
